import { readPhotoMeta } from '../../services/photo/photoMeta';
import { roundAvatarRect } from '../../services/photo/avatarCrop';
import { personColourVar } from '../../services/people/personColour';
import s from './RoundAvatar.module.css';

/**
 * RoundAvatar — the SIGNUP redesign's one small round face (README ask 2):
 * Chats list, chat header, and the Sharing-neighborhood faces all render this.
 *
 * Cut from the person's own photograph on its stored face rectangle — the
 * round face of SYSTEM §8 (face box 41% of the circle, its middle 48% down;
 * avatarCrop.roundAvatarRect). No face found → the picture's
 * centre at cover. A logo or drawing is cut exactly like a photo (user ruling
 * 2026-10-03: no logo mode). No photo → their initial on their colour. A
 * record with no way to reach them → the reserved grey field.
 *
 * A second avatar on purpose: ConnectionAvatar (cover-centred, Royale ring)
 * serves today's screens and must render unchanged on main until the post-flip
 * collapse. Consumes signup-tokens.css (--field-*, --font-display).
 *
 * @param {string} [photoUrl]
 * @param {Object} [photoMeta] - the stored meta (checked against photoUrl)
 * @param {string} name - their first letter is the no-photo mark
 * @param {string} [colour] - their chosen colour (a PERSON_COLOURS key); unset → purple
 * @param {boolean} [hasContact=true] - false → the reserved grey field
 * @param {number} [size=44] - diameter in px
 */
const RoundAvatar = ({
  photoUrl,
  photoMeta,
  name,
  colour,
  hasContact = true,
  size = 44,
  openTile = false,
  onOwnColour = false,
}) => {
  // The People → Detail opening measures this circle as its source (§9a).
  const tile = openTile ? { 'data-open-tile': '' } : null;
  const circle = {
    position: 'relative',
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const field = personColourVar({ colour }, { hasContact });

  if (photoUrl) {
    const meta = readPhotoMeta({ photoUrl, photoMeta });
    const rect = roundAvatarRect(meta, size);
    // Their colour fills the circle behind the picture — a see-through logo or
    // drawing sits on it; a photo covers it. Aspect unknown (legacy photo, until
    // its meta lands) → centred cover.
    return (
      <div style={{ ...circle, background: field }} data-field={field} {...tile}>
        <img
          src={photoUrl}
          alt=""
          style={
            rect
              ? {
                  position: 'absolute',
                  left: rect.left,
                  top: rect.top,
                  width: rect.width,
                  height: rect.height,
                  maxWidth: 'none',
                }
              : {
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }
          }
        />
      </div>
    );
  }

  return (
    <div
      data-field={field}
      {...tile}
      {...(openTile ? { 'data-open-initial': '' } : null)}
      className={s.initial}
      style={{
        ...circle,
        background: field,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        // Reference: people-nearby-block.jsx .av.ini — 19px on a 44px circle.
        fontSize: Math.round(size * 0.43),
        color: 'rgba(255,255,255,0.72)',
        ...(onOwnColour ? { background: 'none' } : null),
      }}
    >
      {onOwnColour ? (
        // On their own colour (Detail's compact bar): the same gradient there
        // melts the circle's top into the bar. The lighter half, flipped —
        // light at the top, the middle tone at the foot — reads against the
        // bar in every colour, no ring (Mark, 2026-10-14; ΔE ≥ 10.5 on all 12
        // brand colours and 7.8 on the no-contact grey, vs 0.2–0.4 today and
        // with a plain flip).
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: field,
            backgroundSize: '100% 200%',
            backgroundPosition: '0 100%',
            transform: 'scaleY(-1)',
          }}
        />
      ) : null}
      <span style={onOwnColour ? { position: 'relative' } : undefined}>
        {name?.trim()[0]?.toUpperCase() || ''}
      </span>
    </div>
  );
};

export default RoundAvatar;
